import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { electronicInvoicingApi } from "../../api/electronicInvoicingApi";

type InvoiceElectronicStatus =
  | "DRAFT"
  | "XML_READY"
  | "SENT_TO_ARUBA"
  | "SENT_TO_SDI"
  | "DELIVERED"
  | "NOT_DELIVERED"
  | "REJECTED"
  | "ERROR";

type InvoiceRow = {
  id: string;
  number: string;
  year: number;
  customerName: string;
  customerVat?: string | null;
  customerFiscalCode?: string | null;
  issueDate: string;
  total: number | string;
  currency?: string;
  electronicStatus?: InvoiceElectronicStatus;
  status?: InvoiceElectronicStatus;
  provider?: string | null;
  externalId?: string | null;
  sdiIdentifier?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

type GenericApiResponse = {
  ok?: boolean;
  message?: string;
  data?: any;
};

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "short",
  }).format(date);
}

function formatAmount(value: unknown, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "—";

  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
  }).format(num);
}

function getStatusLabel(status: InvoiceElectronicStatus) {
  switch (status) {
    case "DRAFT":
      return "Bozza";
    case "XML_READY":
      return "XML pronto";
    case "SENT_TO_ARUBA":
      return "Inviata ad Aruba";
    case "SENT_TO_SDI":
      return "Inviata a SDI";
    case "DELIVERED":
      return "Consegnata";
    case "NOT_DELIVERED":
      return "Non recapitata";
    case "REJECTED":
      return "Scartata";
    case "ERROR":
      return "Errore";
    default:
      return status;
  }
}

function getStatusBadgeClass(status: InvoiceElectronicStatus) {
  switch (status) {
    case "DRAFT":
      return "text-bg-secondary";
    case "XML_READY":
      return "text-bg-info";
    case "SENT_TO_ARUBA":
      return "text-bg-primary";
    case "SENT_TO_SDI":
      return "text-bg-primary";
    case "DELIVERED":
      return "text-bg-success";
    case "NOT_DELIVERED":
      return "text-bg-warning";
    case "REJECTED":
      return "text-bg-danger";
    case "ERROR":
      return "text-bg-danger";
    default:
      return "text-bg-light";
  }
}

function normalizeStatus(item: InvoiceRow): InvoiceElectronicStatus {
  return (item.electronicStatus ?? item.status ?? "DRAFT") as InvoiceElectronicStatus;
}

export default function InvoicesListPage() {
  const { token } = useAuth() as any;
  const [items, setItems] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [busyMap, setBusyMap] = useState<Record<string, boolean>>({});
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "warning" | "info";
    text: string;
  } | null>(null);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setAlert(null);
      const data = await electronicInvoicingApi.listInvoices(token);
      setItems(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore nel caricamento fatture",
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  async function handleGenerateXml(invoiceId: string) {
    try {
      setBusyMap((prev) => ({ ...prev, [invoiceId]: true }));
      setAlert(null);

      const result = (await electronicInvoicingApi.generateXml(token, invoiceId)) as GenericApiResponse;
      setAlert({
        type: "success",
        text: result?.message || "XML generato correttamente.",
      });

      await loadInvoices();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore durante la generazione XML",
      });
    } finally {
      setBusyMap((prev) => ({ ...prev, [invoiceId]: false }));
    }
  }

  async function handleSendToAruba(invoiceId: string) {
    try {
      setBusyMap((prev) => ({ ...prev, [invoiceId]: true }));
      setAlert(null);

      const result = (await electronicInvoicingApi.sendInvoice(token, invoiceId)) as GenericApiResponse;
      setAlert({
        type: "success",
        text: result?.message || "Fattura inviata ad Aruba.",
      });

      await loadInvoices();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore durante invio ad Aruba",
      });
    } finally {
      setBusyMap((prev) => ({ ...prev, [invoiceId]: false }));
    }
  }

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    return items.filter((item) => {
      const status = normalizeStatus(item);
      const matchesQuery =
        !q ||
        item.number?.toLowerCase().includes(q) ||
        String(item.year).includes(q) ||
        item.customerName?.toLowerCase().includes(q) ||
        item.customerVat?.toLowerCase().includes(q) ||
        item.customerFiscalCode?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === "ALL" || status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [items, query, statusFilter]);

  const counters = useMemo(() => {
    return {
      total: items.length,
      delivered: items.filter((x) => normalizeStatus(x) === "DELIVERED").length,
      pending: items.filter((x) =>
        ["XML_READY", "SENT_TO_ARUBA", "SENT_TO_SDI", "NOT_DELIVERED"].includes(normalizeStatus(x)),
      ).length,
      errors: items.filter((x) => ["REJECTED", "ERROR"].includes(normalizeStatus(x))).length,
    };
  }, [items]);

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Fatture NAT</h1>
          <p className="text-muted mb-0">Gestione fatture, generazione XML e invio ad Aruba.</p>
          <p className="small text-muted mb-0">Origine dati: Database locale NAT (`/api/invoices`).</p>
        </div>
        <div>
          <button type="button" className="btn btn-outline-primary" onClick={loadInvoices} disabled={loading}>
            {loading ? "Aggiornamento..." : "Aggiorna elenco"}
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} role="alert">
          {alert.text}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small mb-1">Totale fatture</div>
              <div className="fs-4 fw-semibold">{counters.total}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small mb-1">Consegnate</div>
              <div className="fs-4 fw-semibold">{counters.delivered}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small mb-1">In lavorazione</div>
              <div className="fs-4 fw-semibold">{counters.pending}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small mb-1">Errori / scarti</div>
              <div className="fs-4 fw-semibold">{counters.errors}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-lg-8">
              <label className="form-label">Cerca fattura</label>
              <input
                type="text"
                className="form-control"
                placeholder="Numero, anno, cliente, P.IVA, codice fiscale..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="col-12 col-lg-4">
              <label className="form-label">Stato elettronico</label>
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ALL">Tutti</option>
                <option value="DRAFT">Bozza</option>
                <option value="XML_READY">XML pronto</option>
                <option value="SENT_TO_ARUBA">Inviata ad Aruba</option>
                <option value="SENT_TO_SDI">Inviata a SDI</option>
                <option value="DELIVERED">Consegnata</option>
                <option value="NOT_DELIVERED">Non recapitata</option>
                <option value="REJECTED">Scartata</option>
                <option value="ERROR">Errore</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Cliente</th>
                  <th>Data</th>
                  <th>Totale</th>
                  <th>Stato</th>
                  <th>Provider</th>
                  <th>ID esterno</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      Nessuna fattura trovata.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const busy = !!busyMap[item.id];
                    const status = normalizeStatus(item);
                    const canGenerateXml = status === "DRAFT" || status === "ERROR";
                    const canSend = status === "XML_READY" || status === "NOT_DELIVERED";

                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">
                            <Link to={`/invoices/${item.id}`} className="text-decoration-none">
                              {item.number}/{item.year}
                            </Link>
                          </div>
                        </td>

                        <td>
                          <div>{item.customerName}</div>
                          <div className="small text-muted">{item.customerVat || item.customerFiscalCode || "—"}</div>
                        </td>

                        <td>{formatDate(item.issueDate)}</td>
                        <td>{formatAmount(item.total, item.currency || "EUR")}</td>

                        <td>
                          <span className={`badge ${getStatusBadgeClass(status)}`}>{getStatusLabel(status)}</span>
                        </td>

                        <td>{item.provider || "—"}</td>
                        <td className="small text-muted">{item.externalId || item.sdiIdentifier || "—"}</td>

                        <td>
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={busy || !canGenerateXml}
                              onClick={() => handleGenerateXml(item.id)}
                              title={
                                canGenerateXml
                                  ? "Genera XML FatturaPA"
                                  : "XML gia generato o fattura non modificabile"
                              }
                            >
                              {busy && canGenerateXml ? "Generazione..." : "Genera XML"}
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={busy || !canSend}
                              onClick={() => handleSendToAruba(item.id)}
                              title={
                                canSend ? "Invia fattura ad Aruba" : "Prima genera XML oppure stato non compatibile"
                              }
                            >
                              {busy && canSend ? "Invio..." : "Invia ad Aruba"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
